<script setup>
import { ref, computed, onMounted } from 'vue';
import LoadingSpinner from '@/Components/LoadingSpinner.vue';
import { aiErrorMessage } from '@/composables/aiErrorMessage';

// The "AI" tab on the agent detail page: one switch deciding whether this agent
// sees AI anywhere in the app. Access is off by default — a new agent has no row
// until someone grants it here (or from AI -> Agents in bulk).
//
// The tab also spells out the three layers that have to line up (platform, agency,
// agent), because "the toggle is on but they still see nothing" is otherwise a
// support ticket. When the agency layer is off the switch is disabled outright: it
// would be a control that changes nothing.
const props = defineProps({
    agentId: { type: Number, required: true },
    agentName: { type: String, required: true },
});

const loading = ref(true);
const saving = ref(false);
const enabled = ref(false);
const platformEnabled = ref(false);
const agencyEnabled = ref(false);
const agencyFeatures = ref([]);
const enabledAt = ref(null);
const enabledBy = ref(null);

const canToggle = computed(() => platformEnabled.value && agencyEnabled.value && !saving.value);
const effective = computed(() => platformEnabled.value && agencyEnabled.value && enabled.value);

const layers = computed(() => [
    { label: 'AI platform', ok: platformEnabled.value, hint: 'The module is switched on for TopBroker.' },
    { label: 'This agency', ok: agencyEnabled.value, hint: 'AI is switched on for the agency (AI → Settings).' },
    { label: 'This agent', ok: enabled.value, hint: 'Access has been granted to this agent.' },
]);

function apply(data) {
    enabled.value = !!data.agent?.enabled;
    enabledAt.value = data.agent?.enabled_at ?? null;
    enabledBy.value = data.agent?.enabled_by ?? null;
    platformEnabled.value = !!data.platform_enabled;
    agencyEnabled.value = !!data.agency_enabled;
    agencyFeatures.value = data.agency_features ?? [];
}

async function load() {
    loading.value = true;
    try {
        const { data } = await window.axios.get(route('agents.ai-access.show', props.agentId));
        apply(data);
    } catch (e) {
        window.$notify?.error?.({
            title: 'Could not load AI access',
            message: aiErrorMessage(e, 'Please try again.'),
        });
    } finally {
        loading.value = false;
    }
}

async function toggle() {
    if (!canToggle.value) return;

    const next = !enabled.value;
    saving.value = true;
    try {
        const { data } = await window.axios.put(route('agents.ai-access.update', props.agentId), { enabled: next });
        apply(data);
        window.$notify?.success?.({
            title: next ? `AI turned on for ${props.agentName}` : `AI turned off for ${props.agentName}`,
            duration: 2500,
        });
    } catch (e) {
        window.$notify?.error?.({
            title: 'Could not save AI access',
            message: aiErrorMessage(e, 'Please try again.'),
        });
        // Re-read rather than trusting the failed optimistic value.
        await load();
    } finally {
        saving.value = false;
    }
}

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

onMounted(load);
</script>

<template>
    <div class="max-w-3xl">
        <p v-if="loading" class="inline-flex items-center gap-2 text-sm text-slate-500" role="status" aria-live="polite">
            <LoadingSpinner color-class="text-tb-blue" height-class="h-4" width-class="w-4" />
            Loading…
        </p>

        <template v-else>
            <!-- Master switch -->
            <div class="flex items-start justify-between gap-4 rounded-lg border bg-white p-4">
                <div>
                    <p class="font-medium text-gray-900">AI access</p>
                    <p class="mt-0.5 text-xs text-slate-500">
                        When off, {{ agentName }} sees no AI features anywhere in the app — no lead summaries,
                        suggested replies, priority scores or AI menu.
                    </p>
                    <p v-if="!platformEnabled" class="mt-2 text-xs text-amber-600">
                        AI is switched off for the whole platform.
                    </p>
                    <p v-else-if="!agencyEnabled" class="mt-2 text-xs text-amber-600">
                        AI is off for this agency. Turn it on in <span class="font-semibold">AI → Settings</span> first —
                        until then this switch has no effect.
                    </p>
                </div>

                <label class="relative block shrink-0 cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canToggle }">
                    <span class="sr-only">AI access for {{ agentName }}</span>
                    <input
                        :id="`agent-ai-access-${agentId}`"
                        type="checkbox"
                        class="peer sr-only"
                        :checked="enabled"
                        :disabled="!canToggle"
                        @change="toggle"
                    />
                    <div
                        class="h-6 w-11 rounded-full bg-gray-200 after:absolute after:top-0.5 after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-tb-blue peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"
                    ></div>
                </label>
            </div>

            <!-- Why they can (or can't) see AI -->
            <div class="mt-4 rounded-lg border bg-white p-4">
                <p class="font-medium text-gray-900">Effective access</p>
                <p class="text-xs text-slate-500">All three have to be on before {{ agentName }} sees anything.</p>

                <ul class="mt-3 space-y-2">
                    <li v-for="layer in layers" :key="layer.label" class="flex items-start gap-2 text-sm">
                        <span
                            class="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                            :class="layer.ok ? 'bg-emerald-500' : 'bg-slate-300'"
                            aria-hidden="true"
                        >{{ layer.ok ? '✓' : '✕' }}</span>
                        <span>
                            <span class="text-gray-900">{{ layer.label }}</span>
                            <span class="ml-1 text-xs text-slate-500">{{ layer.hint }}</span>
                        </span>
                    </li>
                </ul>

                <p class="mt-3 text-sm" :class="effective ? 'text-emerald-700' : 'text-slate-600'">
                    {{ effective ? `${agentName} can use AI.` : `${agentName} cannot use AI right now.` }}
                </p>
            </div>

            <!-- What access actually unlocks -->
            <div class="mt-4 rounded-lg border bg-white p-4">
                <p class="font-medium text-gray-900">What this unlocks</p>
                <p class="text-xs text-slate-500">
                    The features this agency currently has switched on. Which features exist is an agency-wide
                    setting; this tab only decides who may use them.
                </p>

                <ul v-if="agencyFeatures.length" class="mt-3 grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <li v-for="f in agencyFeatures" :key="f.key" class="text-sm text-gray-700">• {{ f.label }}</li>
                </ul>
                <p v-else class="mt-3 text-sm text-slate-500">
                    No AI features are switched on for this agency yet.
                </p>
            </div>

            <p v-if="enabled && enabledBy" class="mt-3 text-xs text-slate-500">
                Enabled by {{ enabledBy }}<span v-if="enabledAt"> on {{ formatDate(enabledAt) }}</span>.
            </p>
        </template>
    </div>
</template>
